import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useMutation, createConnectQueryKey } from '@connectrpc/connect-query'
import { useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { create } from '@bufbuild/protobuf'
import {
  listFailedWorkflowTriggerEvents,
  requeueWorkflowTriggerEvent,
} from '../../gen/cortado/v1/admin-AdminService_connectquery'
import { adminListOrganizations } from '../../gen/cortado/v1/cortado-CortadoService_connectquery'
import {
  AdminListOrganizationsRequestSchema,
  ListFailedWorkflowTriggerEventsRequestSchema,
} from '../../gen/cortado/v1/admin_pb'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select'
import {
  SortableHeader,
  compareNumbers,
  compareStrings,
  useTableSort,
} from '../../components/ui/sortable-header'
import { ChevronLeft, ChevronRight, Loader2, RefreshCw, RotateCcw } from 'lucide-react'
import { cn } from '../../lib/utils'

export const Route = createFileRoute('/_admin/workflows')({
  component: AdminWorkflows,
})

const PAGE_SIZE = 50

type EventSortField = 'occurredAt' | 'eventType' | 'organization' | 'retryCount'

const listQueryKey = createConnectQueryKey({
  schema: listFailedWorkflowTriggerEvents,
  cardinality: undefined,
})

function formatOccurredAt(timestamp?: { seconds: bigint }) {
  if (!timestamp) return '-'
  return new Date(Number(timestamp.seconds) * 1000).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}

function AdminWorkflows() {
  const queryClient = useQueryClient()
  const [orgFilter, setOrgFilter] = useState<string>('all')
  const [pageTokens, setPageTokens] = useState<(string | undefined)[]>([undefined])
  const [pageIndex, setPageIndex] = useState(0)
  const { sortField, sortDirection, toggleSort } = useTableSort<EventSortField>('occurredAt', 'desc')

  const orgFilterValue = orgFilter === 'all' ? undefined : orgFilter

  const { data: orgsData } = useQuery(
    adminListOrganizations,
    create(AdminListOrganizationsRequestSchema, {}),
  )
  const orgs = orgsData?.organizations ?? []

  const orgMap = useMemo(() => {
    const map = new Map<string, string>()
    for (const org of orgs) {
      map.set(org.id, org.name)
    }
    return map
  }, [orgs])

  const query = useQuery(
    listFailedWorkflowTriggerEvents,
    create(ListFailedWorkflowTriggerEventsRequestSchema, {
      organizationId: orgFilterValue,
      pageSize: PAGE_SIZE,
      pageToken: pageTokens[pageIndex],
    }),
  )

  const resetPaging = () => {
    setPageTokens([undefined])
    setPageIndex(0)
  }

  const requeueMutation = useMutation(requeueWorkflowTriggerEvent, {
    onSuccess: (data) => {
      if (data.didRequeue) {
        toast.success('Trigger event requeued')
      } else {
        toast.info('Event was not requeued — it is no longer in a failed state')
      }
      queryClient.invalidateQueries({ queryKey: listQueryKey })
    },
    onError: (err: Error) => {
      toast.error(`Requeue failed: ${err.message}`)
    },
  })

  const events = useMemo(() => {
    const list = query.data?.triggerEvents ?? []
    const dir = sortDirection === 'asc' ? 1 : -1
    return [...list].sort((a, b) => {
      switch (sortField) {
        case 'occurredAt':
          return compareNumbers(
            a.occurredAt?.seconds ?? 0n,
            b.occurredAt?.seconds ?? 0n,
          ) * dir
        case 'eventType':
          return compareStrings(a.eventType, b.eventType) * dir
        case 'organization': {
          const aName = orgMap.get(a.organizationId) ?? a.organizationId
          const bName = orgMap.get(b.organizationId) ?? b.organizationId
          return compareStrings(aName, bName) * dir
        }
        case 'retryCount':
          return compareNumbers(a.retryCount, b.retryCount) * dir
        default:
          return 0
      }
    })
  }, [query.data?.triggerEvents, sortField, sortDirection, orgMap])

  const totalCount = query.data?.totalCount ?? 0
  const hasNextPage = Boolean(query.data?.nextPageToken)
  const hasPrevPage = pageIndex > 0

  const handleNextPage = () => {
    const nextToken = query.data?.nextPageToken
    if (!nextToken) return
    setPageTokens((prev) => {
      const updated = [...prev]
      updated[pageIndex + 1] = nextToken
      return updated
    })
    setPageIndex((i) => i + 1)
  }

  const handlePrevPage = () => {
    setPageIndex((i) => Math.max(0, i - 1))
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold">Workflows</h1>
          <p className="text-muted-foreground text-sm">
            Trigger events that exhausted their retries and need a manual requeue
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="w-full sm:w-auto"
          onClick={() => query.refetch()}
          disabled={query.isFetching}
        >
          <RefreshCw className={cn('mr-2 h-3.5 w-3.5', query.isFetching && 'animate-spin')} />
          Refresh
        </Button>
      </div>

      <div className="flex items-center gap-3">
        <Select
          value={orgFilter}
          onValueChange={(value) => {
            setOrgFilter(value)
            resetPaging()
          }}
        >
          <SelectTrigger className="w-full sm:w-[220px]">
            <SelectValue placeholder="All organizations" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All organizations</SelectItem>
            {orgs.map((org) => (
              <SelectItem key={org.id} value={org.id}>
                {org.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <SortableHeader
                  active={sortField === 'occurredAt'}
                  direction={sortDirection}
                  onToggle={() => toggleSort('occurredAt')}
                >
                  Occurred
                </SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader
                  active={sortField === 'eventType'}
                  direction={sortDirection}
                  onToggle={() => toggleSort('eventType')}
                >
                  Event Type
                </SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader
                  active={sortField === 'organization'}
                  direction={sortDirection}
                  onToggle={() => toggleSort('organization')}
                >
                  Organization
                </SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader
                  active={sortField === 'retryCount'}
                  direction={sortDirection}
                  onToggle={() => toggleSort('retryCount')}
                >
                  Retries
                </SortableHeader>
              </TableHead>
              <TableHead>Error</TableHead>
              <TableHead>Payload</TableHead>
              <TableHead className="w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {query.isLoading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground py-12">
                  <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
                  Loading failed trigger events...
                </TableCell>
              </TableRow>
            ) : events.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground py-12">
                  No failed trigger events
                </TableCell>
              </TableRow>
            ) : (
              events.map((event) => (
                <TableRow key={event.id}>
                  <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                    {formatOccurredAt(event.occurredAt)}
                  </TableCell>
                  <TableCell className="text-sm">{event.eventType}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {orgMap.get(event.organizationId) || event.organizationId.slice(0, 8) + '...'}
                  </TableCell>
                  <TableCell>
                    <Badge variant="warning">{event.retryCount}</Badge>
                  </TableCell>
                  <TableCell className="max-w-[220px] truncate text-sm text-muted-foreground" title={event.errorMessage}>
                    {event.errorMessage || '-'}
                  </TableCell>
                  <TableCell
                    className="max-w-[260px] truncate font-mono text-xs text-muted-foreground"
                    title={event.payloadSummary}
                  >
                    {event.payloadSummary}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2"
                      disabled={requeueMutation.isPending}
                      onClick={() => requeueMutation.mutate({ id: event.id })}
                    >
                      <RotateCcw className="h-3.5 w-3.5 mr-1" />
                      Requeue
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-muted-foreground">
          {totalCount > 0
            ? `Showing ${events.length} of ${totalCount} failed trigger events`
            : ''}
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrevPage}
            disabled={!hasPrevPage || query.isFetching}
          >
            <ChevronLeft className="h-3.5 w-3.5 mr-1" />
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleNextPage}
            disabled={!hasNextPage || query.isFetching}
          >
            Next
            <ChevronRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  )
}
